import classNames from "classnames";
import { observer } from "mobx-react";
import React, { useEffect, useMemo } from "react";
import {
  PiBellBold,
  PiChartBarBold,
  PiCheckSquareBold,
  PiFilesBold,
  PiGearBold,
  PiHouseBold,
  PiLightbulbBold,
  PiLineSegmentsBold,
  PiLockBold,
  PiStarBold,
  PiTagBold,
} from "react-icons/pi";
import {
  generatePath,
  Link,
  NavLink,
  useLocation,
  useRouteMatch,
} from "react-router-dom";
import { useIntercom } from "react-use-intercom";

import { userColors } from "@/components/constants";
import { useMainStore } from "@/contexts/Store";
import { PROJECTS_LIST_PATH } from "@/features/projects/pages/ProjectRoutes";
import { TaskCountBadge } from "@/features/tasks/components/TaskCountBadge";
import { FEATURE_FLAG_ID } from "@/stores/types/feature-flag-types";
import { TopAndSubModuleToModuleInfoMap } from "@/stores/types/module-workspaces-types";

import Accordion from "./components/Accordion";
import DashboardMenuItem from "./components/DashboardMenuItem";
import { NavigationLink } from "./components/navigation/NavigationLink";
import SidebarSectionHeader from "./components/SidebarSectionHeader";
import SidebarTag from "./components/SidebarTag";
import WorkspaceNavigation from "./WorkspaceNavigation";

type sectionType = "library" | "workflows" | "tools";

const libraryRoutes = [
  "policy", // Policies
  "procedures", // Procedures
  "documents", // Documents
  "training", // Training
  "control_mapping", // Control Inventory,
  "contracts",
  "accounts",
];

const workflowIdentifiers = [
  "issue_management", // Issue Management
  "marketing", // Marketing
  "new_product_approval", // Change Management
  "vendor_due_diligence", // Vendor Due Diligence
  "risk_assessment", // Questionnaires
  "risk_register", // Risk Register
  "complaints", // Complaints
  "audits", // Audits
  "conflicts_of_interest", // Form Filler
  "qa_tests_development", // Monitoring and Testing
  "key_risk_indicators", // Key Risk Indicators
];

function DashboardMenu() {
  const mainStore = useMainStore();
  const location = useLocation();
  const { activeWorkspace, workspaceID, isIW } = mainStore.context;
  const { user } = mainStore.users;
  const {
    canManageSettings,
    canManageTags,
    canViewReports,
    canManageAccounts,
  } = mainStore.userPermissions;
  const { list: moduleWorkspaces, favoriteModuleWorkspaces } =
    mainStore.moduleWorkspaces;

  const checklistFeatureEnabled = mainStore.featureFlags.getIsEnabled(
    FEATURE_FLAG_ID.COM_CHECKLIST,
  );

  const websiteMonitoringFeatureEnabled = mainStore.featureFlags.getIsEnabled(
    FEATURE_FLAG_ID.COM_WEBSITE_MONITORING,
  );

  const filteredModuleWorkspaces = moduleWorkspaces.filter(
    (moduleWorkspace) => {
      const { identifier } = moduleWorkspace.themis_module;
      if (identifier === "accounts" && !canManageAccounts) {
        return false;
      }
      if (
        identifier === "website_monitoring" &&
        !websiteMonitoringFeatureEnabled
      ) {
        return false;
      }
      if (identifier === "checklists" && !checklistFeatureEnabled) {
        return false;
      }

      return true;
    },
  );
  const countAddedModuleWorkspaces = moduleWorkspaces.filter(
    (mw) => mw.added,
  ).length;
  const moduleInfo = mainStore.moduleWorkspaces
    .moduleInfo as TopAndSubModuleToModuleInfoMap;
  const { isCollaborativeOnly } = mainStore.workspaces;
  const { shutdown } = useIntercom();
  const isReportsMatch = !!useRouteMatch("/workspace-reports");

  const [libraryCollapsed, setLibraryCollapsed] = React.useState(
    localStorage.getItem("libraryCollapsed") === "true",
  );
  const [workflowsCollapsed, setWorkflowsCollapsed] = React.useState(
    localStorage.getItem("workflowsCollapsed") === "true",
  );
  const [toolsCollapsed, setToolsCollapsed] = React.useState(
    localStorage.getItem("toolsCollapsed") === "true",
  );

  const favoriteWorkspacesToRender = useMemo(
    () =>
      favoriteModuleWorkspaces
        .map((favoriteModule) =>
          filteredModuleWorkspaces.find(
            (moduleWorkpsace) =>
              moduleWorkpsace.id === favoriteModule.module_workspace_id,
          ),
        )
        .filter((moduleWorkspace) => moduleWorkspace && moduleWorkspace.added),
    [favoriteModuleWorkspaces, countAddedModuleWorkspaces],
  );

  const libraryWorkspacesToRender = useMemo(
    () =>
      filteredModuleWorkspaces
        .filter(
          (moduleWorkspace) =>
            moduleWorkspace.added &&
            !moduleWorkspace.is_favorited &&
            libraryRoutes.includes(moduleWorkspace.themis_module.identifier),
        )
        .sort(
          (a, b) =>
            libraryRoutes.indexOf(a.themis_module.identifier) -
            libraryRoutes.indexOf(b.themis_module.identifier),
        ),
    [filteredModuleWorkspaces, countAddedModuleWorkspaces],
  );

  const workflowWorkspacesToRender = useMemo(
    () =>
      filteredModuleWorkspaces
        .filter(
          (moduleWorkspace) =>
            moduleWorkspace.added &&
            !moduleWorkspace.is_favorited &&
            workflowIdentifiers.includes(
              moduleWorkspace.themis_module.identifier,
            ),
        )
        .sort(
          (a, b) =>
            workflowIdentifiers.indexOf(a.themis_module.identifier) -
            workflowIdentifiers.indexOf(b.themis_module.identifier),
        ),
    [filteredModuleWorkspaces, countAddedModuleWorkspaces],
  );

  const toolsWorkspacesToRender = useMemo(
    () =>
      filteredModuleWorkspaces.filter(
        (moduleWorkspace) =>
          moduleWorkspace.added &&
          !moduleWorkspace.is_favorited &&
          !libraryRoutes.includes(moduleWorkspace.themis_module.identifier) &&
          !workflowIdentifiers.includes(
            moduleWorkspace.themis_module.identifier,
          ) &&
          moduleInfo[moduleWorkspace.themis_module.identifier],
      ),
    [filteredModuleWorkspaces, countAddedModuleWorkspaces],
  );

  const title = useMemo(() => {
    if (!activeWorkspace) {
      return "";
    }

    return activeWorkspace.is_internal ? "Internal" : "Collaborative";
  }, [activeWorkspace]);

  const triggerTitle = useMemo(() => {
    if (!activeWorkspace) {
      return "Internal";
    }

    return activeWorkspace.is_internal
      ? activeWorkspace.name || activeWorkspace.company.name
      : activeWorkspace.name;
  }, [activeWorkspace]);

  const isLinkSettingsActive = () =>
    location.pathname.startsWith("/settings/") &&
    !location.pathname.includes("/user/profile") &&
    !location.pathname.includes("/user/integrations");

  const isLinkTemplatesActive = location.pathname.startsWith(
    "/resources/compliance-templates",
  );

  const tagsShouldBeVisible =
    !(activeWorkspace && !activeWorkspace.is_internal) && canManageTags;

  const toolsWorkspacesExist =
    (isIW && checklistFeatureEnabled) ||
    isCollaborativeOnly ||
    (isIW && websiteMonitoringFeatureEnabled) ||
    toolsWorkspacesToRender.length > 0;

  const libraryWorkspacesExist =
    libraryWorkspacesToRender.length > 0 ||
    canManageAccounts ||
    tagsShouldBeVisible;

  function handleSignOut(event: React.MouseEvent<HTMLAnchorElement>) {
    event.preventDefault();

    mainStore.webSessions.delete();
    shutdown();
  }

  function setCollapseState(section: sectionType) {
    switch (section) {
      case "library":
        setLibraryCollapsed((prev) => !prev);
        localStorage.setItem("libraryCollapsed", `${!libraryCollapsed}`);
        break;
      case "workflows":
        setWorkflowsCollapsed((prev) => !prev);
        localStorage.setItem("workflowsCollapsed", `${!workflowsCollapsed}`);
        break;
      default:
        setToolsCollapsed((prev) => !prev);
        localStorage.setItem("toolsCollapsed", `${!toolsCollapsed}`);
    }
  }
  useEffect(() => {}, []);

  return (
    <div className="dashboard-menu">
      <div className="workspace-navigation">
        <WorkspaceNavigation />
      </div>
      <div className="dashboard-menu-list">
        <div className="dashboard-name">
          <div
            className="dashboard-name-block"
            data-testid="dashboard-name-block"
          >
            <label className="tw-text-lg tw-font-semibold tw-text-neutral-25">
              {triggerTitle}
            </label>
            <label className="tw-text-xs tw-font-medium tw-text-neutral-25">
              {title}
            </label>
          </div>
          <div className="workspace-owner-container">
            <label className="tw-text-xs tw-font-medium tw-text-primaryDim-300">
              Workspace Owner
            </label>
            <label className="tw-text-sm tw-font-semibold tw-text-primaryDim-300">
              {activeWorkspace?.company?.name}
            </label>
          </div>
        </div>
        <ul className="settings-links" data-testid="settings-nav">
          <li
            data-testid="home-nav"
            data-tooltip-id="tooltip"
            data-tooltip-place="right"
            data-tooltip-content="Module summaries"
          >
            <NavLink to={`/workspaces/${workspaceID}/home`}>
              <PiHouseBold className="tw-h-4 tw-w-4 tw-stroke-[#6870b4]" />
              <span>Home</span>
            </NavLink>
          </li>
        </ul>
        <ul className="settings-links" data-testid="settings-nav">
          <li
            data-testid="my-tasks-nav"
            data-tooltip-id="tooltip"
            data-tooltip-content="View tasks assigned to you and others"
            data-tooltip-place="right"
          >
            <NavLink
              to="/my-tasks"
              className="tw-flex tw-items-center tw-justify-between"
            >
              <span className="tw-flex tw-items-center">
                <PiCheckSquareBold className="tw-h-4 tw-w-4 tw-stroke-[#6870b4]" />
                <span>My Tasks</span>
              </span>
              <TaskCountBadge />
            </NavLink>
          </li>

          <li
            data-testid="notifications-nav"
            data-tooltip-id="tooltip"
            data-tooltip-content="View your assigned comments and approvals"
            data-tooltip-place="right"
          >
            <NavLink to="/notifications/reviews">
              <PiBellBold className="tw-h-4 tw-w-4 tw-stroke-[#6870b4]" />
              <span>Notifications</span>
            </NavLink>
          </li>

          {canManageSettings && (
            <li
              data-tooltip-id="tooltip"
              data-tooltip-content="Manage users, modules, and more"
              data-tooltip-place="right"
            >
              <NavLink
                to="/settings"
                data-testid="settings-nav-link"
                isActive={isLinkSettingsActive}
              >
                <PiGearBold className="tw-h-4 tw-w-4 tw-stroke-[#6870b4]" />
                <span>Settings</span>
              </NavLink>
            </li>
          )}

          {canViewReports && (
            <li
              data-tooltip-id="tooltip"
              data-tooltip-content="View and export reports"
              data-tooltip-place="right"
            >
              <NavLink to="/workspace-reports" replace={isReportsMatch}>
                <PiChartBarBold className="tw-h-4 tw-w-4 tw-stroke-[#6870b4]" />
                <span>Reports</span>
              </NavLink>
            </li>
          )}

          {tagsShouldBeVisible && (
            <li
              data-tooltip-id="tooltip"
              data-tooltip-content="Manage organization-wide tags"
              data-tooltip-place="right"
            >
              <NavLink to="/tags">
                <PiTagBold className="tw-h-4 tw-w-4 tw-stroke-[#6870b4]" />
                <span>Tags</span>
              </NavLink>
            </li>
          )}
        </ul>
        <hr />

        <SidebarSectionHeader
          title="Favorites"
          icon={
            <PiStarBold className="tw-mr-1 tw-h-5 tw-w-5 tw-stroke-neutral-500" />
          }
        />
        <ul className="dashboard-menu-list-links">
          {favoriteWorkspacesToRender
            .filter((moduleWorkspace) => !!moduleWorkspace)
            .map((moduleWorkspace) => {
              if (!moduleWorkspace) {
                return null;
              }

              return (
                <DashboardMenuItem
                  key={`favorite-module-${moduleWorkspace.id}`}
                  moduleInfo={moduleInfo}
                  moduleWorkspace={moduleWorkspace}
                />
              );
            })}
        </ul>

        <hr />

        {libraryWorkspacesExist && (
          <>
            <SidebarSectionHeader
              title="Library"
              icon={
                <PiFilesBold className="tw-mr-1 tw-h-5 tw-w-5 tw-stroke-neutral-500" />
              }
              setOpen={() => setCollapseState("library")}
              open={libraryCollapsed}
            />
            <Accordion collapsed={libraryCollapsed}>
              <ul className="dashboard-menu-list-links">
                {libraryWorkspacesToRender.map((moduleWorkspace) => (
                  <DashboardMenuItem
                    key={`library-module-${moduleWorkspace.id}`}
                    moduleInfo={moduleInfo}
                    moduleWorkspace={moduleWorkspace}
                  />
                ))}
              </ul>
            </Accordion>

            <hr />
          </>
        )}

        {workflowWorkspacesToRender.length > 0 && (
          <>
            <SidebarSectionHeader
              title="Workflows"
              icon={
                <PiLineSegmentsBold className="tw-mr-1 tw-h-5 tw-w-5 tw-stroke-neutral-500" />
              }
              setOpen={() => setCollapseState("workflows")}
              open={workflowsCollapsed}
            />
            <Accordion collapsed={workflowsCollapsed}>
              <ul className="dashboard-menu-list-links">
                {mainStore.featureFlags.getIsEnabled(
                  FEATURE_FLAG_ID.APP_PROJECTS,
                ) && (
                  <NavigationLink
                    url={generatePath(PROJECTS_LIST_PATH, {
                      workspace_id: workspaceID!.toString(),
                    })}
                    name="Projects"
                    moduleID="projects"
                  />
                )}
                {workflowWorkspacesToRender.map((moduleWorkspace) => (
                  <DashboardMenuItem
                    key={`workflow-module-${moduleWorkspace.id}`}
                    moduleInfo={moduleInfo}
                    moduleWorkspace={moduleWorkspace}
                  />
                ))}
              </ul>
            </Accordion>
            <hr />
          </>
        )}

        {toolsWorkspacesExist && (
          <>
            <SidebarSectionHeader
              title="Tools"
              icon={
                <PiLockBold className="tw-mr-1 tw-h-5 tw-w-5 tw-stroke-neutral-500" />
              }
              setOpen={() => setCollapseState("tools")}
              open={toolsCollapsed}
            />
            <Accordion collapsed={toolsCollapsed}>
              <ul className="dashboard-menu-list-links">
                {toolsWorkspacesToRender.map((moduleWorkspace) => (
                  <DashboardMenuItem
                    key={`tool-module-${moduleWorkspace.id}`}
                    moduleInfo={moduleInfo}
                    moduleWorkspace={moduleWorkspace}
                  />
                ))}
                {isCollaborativeOnly && (
                  <li>
                    <Link
                      to="/resources/compliance-templates"
                      className={classNames("dashboard-menu-compliance-link", {
                        active: isLinkTemplatesActive,
                      })}
                    >
                      Templates
                      <SidebarTag bgColor="fuchsia-1" textColor="fuchsia-3">
                        New
                      </SidebarTag>
                    </Link>
                  </li>
                )}
              </ul>
            </Accordion>

            <hr />
          </>
        )}

        <SidebarSectionHeader
          title="Authentication"
          icon={
            <PiLightbulbBold className="tw-mr-1 tw-h-5 tw-w-5 tw-stroke-neutral-500" />
          }
        />
        <div
          className="dashboard-settings"
          data-tooltip-id="tooltip"
          data-tooltip-content="User settings and integrations"
          data-tooltip-place="right"
        >
          <NavLink to="/user">
            <span
              style={{
                background:
                  userColors[mainStore.users.user.icon_color_index || 0],
                minWidth: 20,
                minHeight: 20,
              }}
              className="user-initials"
            >
              {user.initials}
            </span>
            <span className="user-full-name">{user.full_name}</span>
          </NavLink>
        </div>
        <ul className="dashboard-menu-list-links">
          {mainStore.webSessions.accessToken ? (
            <li>
              <Link
                to="/sign-out"
                onClick={handleSignOut}
                data-testid="sign-out"
              >
                Sign Out
              </Link>
            </li>
          ) : (
            <>
              <li>
                <Link to="/sign-in">Sign In</Link>
              </li>
              <li>
                <Link to="/sign-up">Sign Up</Link>
              </li>
            </>
          )}
        </ul>
      </div>
    </div>
  );
}

export default observer(DashboardMenu);
